import { jsonObject, recordedString } from '@/types/json.types'
import {
  recordedTime,
  recordedIso,
  formatRecordedDate,
  recordedBooleanLabel,
} from '@/utils/recordedValues'
/**
 * CustomerActivityTab — activity timeline for a single customer (TASK-01d).
 * Reuses the audit service (`listForResource`) which hits the security-service
 * `/audit/resources/{type}/{id}` endpoint.
 *
 * Features:
 *  - Filtering by actor (user / system)
 *  - Filtering by action type
 *  - Filtering by date range (preset or custom)
 *  - Search query across descriptions and actors
 *  - CSV export of filtered logs
 *  - Firm-wide audit view link (for compliance)
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Clock,
  Activity,
  CheckCircle,
  XCircle,
  User,
  Search,
  Filter,
  Download,
  ExternalLink,
  RotateCcw,
} from 'lucide-react'
import { getAuditService } from '@/services/audit'
import { toast } from '@/store/toastStore'
import { useAuth } from '@/contexts/AuthContext'
import { getAuditActionDisplayName, formatRelativeTime } from '@/types/session.types'
import type { AuditLogEntry } from '@/types/session.types'
import type { IndividualCustomer } from '@/types/customer'

const RESOURCE_TYPE = 'customer'

interface CustomerActivityTabProps {
  customer: IndividualCustomer
}

function describe(entry: AuditLogEntry): string {
  const d = jsonObject(entry.details)
  return (
    recordedString(d.description) ||
    recordedString(d.message) ||
    getAuditActionDisplayName(entry.action)
  )
}

function formatTimestamp(iso: string | null): string {
  return formatRecordedDate(iso, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function CustomerActivityTab({ customer }: CustomerActivityTabProps) {
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const canViewFirmAudit = user?.role === 'firm_owner' || user?.role === 'firm_admin'
  const canExport = hasPermission('txh.audit.export')

  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  // Filter states
  const [searchQuery, setSearchQuery] = useState('')
  const [filterActor, setFilterActor] = useState('all')
  const [filterAction, setFilterAction] = useState('all')
  const [filterDateRange, setFilterDateRange] = useState<
    'all' | 'today' | '7days' | '30days' | 'custom'
  >('all')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const items = await getAuditService().listForResource(RESOURCE_TYPE, customer.id)
      setEntries(items)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Please try again.')
      toast.error(
        'Could not load activity',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [customer.id])

  useEffect(() => {
    load()
  }, [load])

  // Derive unique actors
  const actors = useMemo(() => {
    const map = new Map<string, string>()
    for (const e of entries) {
      if (e.performedByName) {
        map.set(e.userId || e.performedByName, e.performedByName)
      } else if (e.userEmail) {
        map.set(e.userId || e.userEmail, e.userEmail)
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [entries])

  // Derive unique action types
  const actionTypes = useMemo(() => {
    const set = new Set<string>()
    for (const e of entries) {
      if (e.action) set.add(e.action)
    }
    return Array.from(set).map(act => ({
      action: act,
      label: getAuditActionDisplayName(act as any),
    }))
  }, [entries])

  // Filtered entries
  const filteredEntries = useMemo(() => {
    const now = Date.now()
    return entries.filter(entry => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase()
        const text =
          `${describe(entry)} ${entry.performedByName || ''} ${entry.userEmail || ''} ${entry.action}`.toLowerCase()
        if (!text.includes(q)) return false
      }

      // Actor
      if (filterActor !== 'all') {
        if (filterActor === 'system') {
          if (entry.performedByName || entry.userEmail) return false
        } else {
          const matchesActor =
            entry.performedByName === filterActor ||
            entry.userId === filterActor ||
            entry.userEmail === filterActor
          if (!matchesActor) return false
        }
      }

      // Action type
      if (filterAction !== 'all' && entry.action !== filterAction) {
        return false
      }

      // Date range
      const recorded = recordedTime(entry.createdAt)
      if (filterDateRange !== 'all' && recorded === null) return false
      const entryTime = recorded ?? NaN
      if (filterDateRange === 'today') {
        const startOfDay = new Date().setHours(0, 0, 0, 0)
        if (entryTime < startOfDay) return false
      } else if (filterDateRange === '7days') {
        if (entryTime < now - 7 * 86400000) return false
      } else if (filterDateRange === '30days') {
        if (entryTime < now - 30 * 86400000) return false
      } else if (filterDateRange === 'custom') {
        if (customFrom && entryTime < new Date(customFrom).getTime()) return false
        if (customTo && entryTime > new Date(customTo).setHours(23, 59, 59, 999)) return false
      }

      return true
    })
  }, [entries, searchQuery, filterActor, filterAction, filterDateRange, customFrom, customTo])

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    filterActor !== 'all' ||
    filterAction !== 'all' ||
    filterDateRange !== 'all'

  const clearFilters = () => {
    setSearchQuery('')
    setFilterActor('all')
    setFilterAction('all')
    setFilterDateRange('all')
    setCustomFrom('')
    setCustomTo('')
  }

  const handleExportCSV = () => {
    if (filteredEntries.length === 0) {
      toast.error('No entries to export')
      return
    }

    const headers = ['Timestamp', 'Action', 'Actor', 'Status', 'Description', 'IP Address']
    const rows = filteredEntries.map(e => [
      `"${recordedIso(e.createdAt)}"`,
      `"${e.action}"`,
      `"${e.performedByName || e.userEmail || 'System'}"`,
      `"${recordedBooleanLabel(e.success, 'Success', 'Failed')}"`,
      `"${describe(e).replace(/"/g, '""')}"`,
      `"${e.ipAddress || ''}"`,
    ])

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `activity-log-${customer.pan || customer.id}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Activity log exported', `${filteredEntries.length} entries downloaded`)
  }

  return (
    <div className="space-y-4">
      {/* Header with Title and Firm-wide View / Export actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-text-primary">Activity Log</h3>

        <div className="flex items-center gap-2 flex-wrap">
          {canViewFirmAudit && (
            <button
              type="button"
              onClick={() => navigate('/audit')}
              className="btn btn-ghost btn-sm gap-1.5 text-text-secondary"
              title="View firm-wide audit trail across all customers"
            >
              <ExternalLink className="w-4 h-4" />
              Firm-wide Audit Log
            </button>
          )}

          {canExport && (
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={filteredEntries.length === 0}
              className="btn btn-outline btn-sm gap-1.5"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Filter Controls Toolbar */}
      <div className="card card-elevated p-6 space-y-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
          {/* Search Input */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search activity..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="input input-bordered w-full pl-8 text-sm"
              aria-label="Search activity"
            />
          </div>

          {/* Actor Filter */}
          <select
            value={filterActor}
            onChange={e => setFilterActor(e.target.value)}
            className="input input-bordered text-sm min-w-[130px]"
            aria-label="Filter by Actor"
          >
            <option value="all">All Actors</option>
            <option value="system">System Only</option>
            {actors.map(a => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          {/* Action Type Filter */}
          <select
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="input input-bordered text-sm min-w-[130px]"
            aria-label="Filter by Action Type"
          >
            <option value="all">All Action Types</option>
            {actionTypes.map(act => (
              <option key={act.action} value={act.action}>
                {act.label}
              </option>
            ))}
          </select>

          {/* Date Range Filter */}
          <select
            value={filterDateRange}
            onChange={e => setFilterDateRange(e.target.value as any)}
            className="input input-bordered text-sm min-w-[120px]"
            aria-label="Filter by Date Range"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="btn btn-ghost btn-sm gap-1 text-xs"
              title="Reset all filters"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
        </div>

        {/* Custom Date Range Inputs */}
        {filterDateRange === 'custom' && (
          <div className="flex items-center gap-2 pt-2 border-t border-surface-overlay text-xs flex-wrap">
            <span className="text-text-secondary font-medium">From:</span>
            <input
              type="date"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              className="input input-bordered input-xs"
            />
            <span className="text-text-secondary font-medium">To:</span>
            <input
              type="date"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              className="input input-bordered input-xs"
            />
          </div>
        )}

        {/* Status Bar */}
        <div className="flex items-center justify-between text-sm text-text-tertiary pt-1">
          <div className="flex items-center gap-1.5">
            <Filter className="w-3 h-3" />
            <span>
              Showing {filteredEntries.length} of {entries.length} events
            </span>
          </div>
        </div>
      </div>

      {/* Activity Timeline List */}
      {loading ? (
        <div className="card card-elevated p-4 space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-start gap-3">
              <div className="skeleton w-8 h-8 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-1/2 rounded" />
                <div className="skeleton h-3 w-1/3 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div role="alert" className="card card-elevated p-6 space-y-3">
          <p className="text-red-600 dark:text-red-400">Could not load activity: {loadError}</p>
          <button type="button" className="btn btn-outline" onClick={load}>
            Retry
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="card card-elevated p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-surface-overlay flex items-center justify-center mx-auto mb-4">
            <Activity className="w-8 h-8 text-text-tertiary" />
          </div>
          <p className="text-lg font-medium text-text-primary mb-2">No activity yet</p>
          <p className="text-text-secondary">Actions taken on this client will appear here.</p>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="card card-elevated p-8 text-center">
          <Activity className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
          <p className="text-sm font-medium text-text-primary mb-1">No matching activity</p>
          <p className="text-sm text-text-secondary mb-3">
            No events match your current filter criteria.
          </p>
          <button type="button" onClick={clearFilters} className="btn btn-outline btn-xs">
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="card card-elevated p-4">
          <ol className="relative border-s border-surface-overlay ms-3">
            {filteredEntries.map(entry => (
              <li key={entry.id} className="mb-6 ms-6 last:mb-0">
                <span
                  className={`absolute -start-3 flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white dark:ring-surface-dark ${
                    entry.success === true
                      ? 'bg-green-100 dark:bg-green-900/30'
                      : entry.success === false
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : 'bg-surface-overlay'
                  }`}
                >
                  {entry.success === true ? (
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                  ) : entry.success === false ? (
                    <XCircle className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                  ) : (
                    <Clock
                      className="w-3.5 h-3.5 text-text-tertiary"
                      aria-label="Result not recorded"
                    />
                  )}
                </span>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <p className="text-sm font-medium text-text-primary">{describe(entry)}</p>
                  <time className="text-sm text-text-tertiary flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {formatTimestamp(entry.createdAt)}
                  </time>
                </div>

                <div className="flex items-center gap-2 mt-1 text-sm text-text-tertiary flex-wrap">
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {entry.performedByName || entry.userEmail || 'System'}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{formatRelativeTime(entry.createdAt)}</span>
                  {entry.action && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="bg-surface-overlay px-1.5 py-0.2 rounded text-[10px] font-mono">
                        {entry.action}
                      </span>
                    </>
                  )}
                </div>

                {entry.errorMessage && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                    {entry.errorMessage}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}
